<template>
  <div class="editorial-b-wrap">

    <header class="ed-header">
      <div class="left-section">
        <button type="button" class="hamburger-menu" aria-label="Toggle menu">☰</button>
        <nav class="lang-switcher" aria-label="Language">
          <button type="button" class="lang-btn active" aria-pressed="true">EN</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">DE</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">FR</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">IT</button>
        </nav>
      </div>
      <div class="brand">
        <span class="brand-title">Swiss Eating Quiz</span>
        <span class="brand-version">Beta · 03.05.2026</span>
      </div>
    </header>

    <div class="screen-wrap">
      <main class="screen" role="main">

        <div class="meta-row">
          <button
            type="button"
            class="back-button"
            :disabled="!canGoBack"
            aria-label="Zurück"
            @click="goBack"
          >←</button>
          <span class="section-counter">
            Abschnitt {{ paddedIndex }} / {{ paddedTotal }}
          </span>
        </div>

        <div
          class="progress-bar"
          role="progressbar"
          :aria-valuenow="currentSectionIndex + 1"
          aria-valuemin="1"
          :aria-valuemax="sectionsWithQuestions.length"
          aria-label="Fortschritt"
        >
          <div
            v-for="seg in progressSegments"
            :key="seg.sectionId"
            class="progress-segment"
          >
            <div
              v-if="seg.fill > 0"
              class="progress-segment-fill"
              :style="{ width: (seg.fill * 100) + '%' }"
            ></div>
          </div>
        </div>

        <p class="section-eyebrow">Lebensmittelgruppe {{ ordinalLabel }}</p>

        <div class="section-header">
          <h1 class="section-title">{{ currentSection.label }}</h1>
          <p class="section-prompt">{{ currentSection.prompt }}</p>
          <p class="section-progress-text">
            {{ answeredInSection }} / {{ currentSection.questions.length }} beantwortet
          </p>
        </div>

        <div class="question-list">
          <div
            v-for="(q, i) in currentSection.questions"
            :key="q.id"
            class="question-row"
          >
            <div class="question-row-header">
              <span class="question-row-number">{{ pad2(i + 1) }}</span>
              <h2 class="question-row-title">{{ q.title }}</h2>
              <button
                v-if="q.tooltip"
                type="button"
                class="tooltip-button-small"
                :aria-expanded="String(!!tooltipsOpen[q.id])"
                :aria-controls="`tt-${q.id}`"
                @click="toggleTooltip(q.id)"
              >i</button>
            </div>
            <div
              v-if="q.tooltip && tooltipsOpen[q.id]"
              :id="`tt-${q.id}`"
              class="tooltip-content"
            >
              {{ q.tooltip }}
            </div>
            <div class="answer-chips" role="radiogroup" :aria-label="q.title">
              <label
                v-for="opt in q.options"
                :key="opt.value"
                class="chip"
              >
                <input
                  type="radio"
                  :name="`q-${q.id}`"
                  :value="opt.value"
                  v-model="answers[q.id]"
                >
                <span class="chip-label">{{ opt.compactLabel || opt.label }}</span>
              </label>
            </div>
          </div>
        </div>

      </main>
    </div>

    <footer class="footer">
      <div class="footer-inner">
        <p class="footer-progress">
          <strong>{{ answeredInSection }}</strong> / {{ currentSection.questions.length }} beantwortet
        </p>
        <button
          type="button"
          class="next-button"
          :disabled="!isComplete"
          @click="goNext"
        >{{ nextButtonLabel }}</button>
      </div>
    </footer>

  </div>
</template>

<script>
import { sections, questions } from '../_data/sampleQuestions.js';

const ORDINALS = ['Eins', 'Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben'];

function pad2(n) {
  return n < 10 ? `0${n}` : String(n);
}

export default {
  name: 'EditorialB',
  data() {
    return {
      currentSectionIndex: 0,
      tooltipsOpen: {},
      answers: {},
    };
  },
  computed: {
    sectionsWithQuestions() {
      return sections
        .map(s => ({ ...s, questions: questions.filter(q => q.sectionId === s.id) }))
        .filter(s => s.questions.length > 0);
    },
    currentSection() {
      return this.sectionsWithQuestions[this.currentSectionIndex];
    },
    answeredInSection() {
      return this.currentSection.questions.filter(
        q => this.answers[q.id] !== undefined,
      ).length;
    },
    isComplete() {
      return this.answeredInSection === this.currentSection.questions.length;
    },
    isLastSection() {
      return this.currentSectionIndex === this.sectionsWithQuestions.length - 1;
    },
    canGoBack() {
      return this.currentSectionIndex > 0;
    },
    nextButtonLabel() {
      if (this.isLastSection) return 'Fertig →';
      const nextSection = this.sectionsWithQuestions[this.currentSectionIndex + 1];
      return `Weiter zu ${nextSection.label} →`;
    },
    progressSegments() {
      return this.sectionsWithQuestions.map((s, i) => {
        let fill = 0;
        if (i < this.currentSectionIndex) {
          fill = 1;
        } else if (i === this.currentSectionIndex && s.questions.length > 0) {
          fill = this.answeredInSection / s.questions.length;
        }
        return { sectionId: s.id, fill };
      });
    },
    paddedIndex() {
      return pad2(this.currentSectionIndex + 1);
    },
    paddedTotal() {
      return pad2(this.sectionsWithQuestions.length);
    },
    ordinalLabel() {
      return ORDINALS[this.currentSectionIndex] || String(this.currentSectionIndex + 1);
    },
  },
  methods: {
    pad2,
    toggleTooltip(qid) {
      this.tooltipsOpen = { ...this.tooltipsOpen, [qid]: !this.tooltipsOpen[qid] };
    },
    goBack() {
      if (!this.canGoBack) return;
      this.currentSectionIndex--;
      this.tooltipsOpen = {};
      this.scrollToTop();
    },
    goNext() {
      if (!this.isComplete) return;
      if (this.isLastSection) {
        this.$router.push('/postsurvey/email');
        return;
      }
      this.currentSectionIndex++;
      this.tooltipsOpen = {};
      this.scrollToTop();
    },
    scrollToTop() {
      this.$nextTick(() => {
        if (this.$el && typeof this.$el.scrollIntoView === 'function') {
          this.$el.scrollIntoView({ block: 'start' });
        }
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

.editorial-b-wrap {
  --swiss-red: #DA291C;
  --swiss-red-deep: #B01E13;
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-faint: #9C9C9C;
  --paper: #FAF7F2;
  --paper-warm: #F2EDE4;
  --paper-card: #FFFFFF;
  --rule-soft: #E5E0D7;
  --good: #2D8659;
  --font-display: "Fraunces", Georgia, serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", "SF Mono", Consolas, monospace;

  font-family: var(--font-body);
  background: var(--paper);
  color: var(--ink);
  line-height: 1.55;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100%;
  box-sizing: border-box;
}

.editorial-b-wrap *,
.editorial-b-wrap *::before,
.editorial-b-wrap *::after {
  box-sizing: border-box;
}

.ed-header {
  border-bottom: 1px solid var(--ink);
  background: var(--paper);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hamburger-menu {
  background: transparent;
  border: 0.5px solid var(--rule-soft);
  color: var(--ink);
  font-size: 18px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
}

.lang-btn {
  background: transparent;
  border: none;
  color: var(--ink-faint);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  padding: 2px 4px;
}

.lang-btn.active {
  color: var(--swiss-red);
  font-weight: 600;
}

.lang-sep { color: var(--ink-faint); }

.brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.brand-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.brand-version {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.screen-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 20px 120px;
}

.screen {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 18px;
  color: var(--ink-mute);
  line-height: 1;
  margin-left: -8px;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.section-counter {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 36px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--rule-soft);
}

.progress-segment {
  height: 3px;
  background: var(--paper-warm);
  overflow: hidden;
  flex: 1;
}

.progress-segment-fill {
  height: 100%;
  background: var(--swiss-red);
  transition: width 0.3s ease;
}

.section-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--swiss-red);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-eyebrow::before {
  content: "";
  width: 24px;
  height: 1px;
  background: var(--swiss-red);
}

.section-header {
  margin-bottom: 32px;
}

.section-title {
  font-family: var(--font-display);
  font-size: clamp(48px, 9vw, 64px);
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin: 0 0 18px;
  font-variation-settings: "opsz" 144;
  color: var(--ink);
}

.section-prompt {
  font-size: 17px;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 0 0 12px;
}

.section-progress-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}

.question-list {
  border-top: 1px solid var(--ink);
  margin-bottom: 32px;
}

.question-row {
  padding: 22px 0;
  border-bottom: 0.5px solid var(--rule-soft);
}

.question-row-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
}

.question-row-number {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-faint);
  letter-spacing: 0.06em;
  flex-shrink: 0;
  width: 28px;
}

.question-row-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--ink);
  flex: 1;
  font-variation-settings: "opsz" 24;
  margin: 0;
}

.tooltip-button-small {
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--swiss-red);
  color: white;
  font-size: 10px;
  font-weight: 600;
  font-style: italic;
  font-family: var(--font-display);
  line-height: 1;
  align-self: center;
}

.tooltip-button-small:hover {
  background: var(--swiss-red-deep);
}

.tooltip-content {
  background: var(--paper-warm);
  border-left: 2px solid var(--swiss-red);
  padding: 10px 12px;
  font-size: 13px;
  color: var(--ink-soft);
  margin-bottom: 14px;
  line-height: 1.5;
}

.answer-chips {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: 0.5px solid var(--rule-soft);
}

.chip {
  position: relative;
  border-right: 0.5px solid var(--rule-soft);
}

.chip:last-child {
  border-right: none;
}

.chip input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.chip-label {
  display: block;
  text-align: center;
  padding: 14px 4px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  cursor: pointer;
  background: transparent;
  transition: background 0.15s, color 0.15s;
  line-height: 1.3;
  text-transform: uppercase;
}

.chip-label:hover {
  background: var(--paper-warm);
  color: var(--ink);
}

.chip input:checked + .chip-label {
  background: var(--swiss-red);
  color: white;
  font-weight: 500;
}

.footer {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--paper);
  border-top: 1px solid var(--ink);
  padding: 16px 20px;
  z-index: 40;
}

.footer-inner {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-progress {
  flex: 1;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}

.footer-progress strong {
  color: var(--ink);
  font-weight: 600;
}

.next-button {
  padding: 14px 28px;
  background: var(--ink);
  color: var(--paper);
  border: 1px solid var(--ink);
  border-radius: 0;
  font-size: 13px;
  font-family: var(--font-mono);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}

.next-button:disabled {
  background: var(--paper-warm);
  color: var(--ink-faint);
  border-color: var(--rule-soft);
  cursor: not-allowed;
}

.next-button:not(:disabled):hover {
  background: var(--swiss-red);
  border-color: var(--swiss-red);
}
</style>
